type TimedTranscriptUnit = {
    id: string;
    startMs: number;
    endMs: number;
    text: string;
    speakerName?: string;
};

type TimedTranscriptWord = {
    startMs: number;
    endMs: number;
    text: string;
    speakerName?: string;
};

type RangeCandidate = {
    startMs: number;
    endMs: number;
    startUnitId?: string;
    endUnitId?: string;
};

type RefinedRange = {
    startMs: number;
    endMs: number;
};

const EPS_MS = 5;
const START_UNIT_SNAP_BACK_MS = 180;
const START_PREROLL_MS = 120;
const START_MIN_GAP_MS = 70;
const END_DEFAULT_TAIL_MS = 180;
const END_MAX_EXTENSION_MS = 1200;
const END_UNIT_SNAP_EXTENSION_MS = 900;
const END_MIN_GAP_MS = 70;
const END_MAX_TAIL_INTO_GAP_MS = 220;
const END_PAUSE_MS = 280;
const END_TURN_GAP_MS = 120;
const MAX_WORD_SCAN_AHEAD = 24;

const clampPositiveMs = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.round(value));
};

const getGapMs = (
    currentEndMs: number,
    nextStartMs: number | null | undefined,
): number => {
    if (!Number.isFinite(currentEndMs) || !Number.isFinite(Number(nextStartMs))) return Number.POSITIVE_INFINITY;
    return Math.max(0, Number(nextStartMs) - currentEndMs);
};

const isHardStopText = (text: string): boolean => /[.!?]["')\]]*$/.test(String(text ?? "").trim());
const isSoftStopText = (text: string): boolean => /[,;:]["')\]]*$/.test(String(text ?? "").trim());

const tailIntoGapMs = (gapMs: number): number => {
    if (!Number.isFinite(gapMs)) return END_DEFAULT_TAIL_MS;
    if (gapMs < END_MIN_GAP_MS) return 0;
    return Math.min(END_MAX_TAIL_INTO_GAP_MS, Math.max(90, Math.round(gapMs * 0.4)));
};

const clampTailWithinGap = (endMs: number, gapMs: number): number => {
    const tailMs = tailIntoGapMs(gapMs);
    if (!Number.isFinite(gapMs)) return clampPositiveMs(endMs + tailMs);
    return clampPositiveMs(endMs + Math.min(tailMs, Math.max(0, gapMs - 40)));
};

const findUnitIndex = (
    units: TimedTranscriptUnit[],
    unitId: string | undefined,
    atMs: number,
): number => {
    const normalizedId = String(unitId ?? "").trim();
    if (normalizedId) {
        const byId = units.findIndex((unit) => String(unit?.id ?? "").trim() === normalizedId);
        if (byId >= 0) return byId;
    }

    for (let index = 0; index < units.length; index += 1) {
        const unit = units[index];
        if (!unit) continue;
        if (atMs >= Number(unit.startMs ?? 0) - EPS_MS && atMs <= Number(unit.endMs ?? 0) + EPS_MS) {
            return index;
        }
    }

    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < units.length; index += 1) {
        const unit = units[index];
        if (!unit) continue;
        const startDistance = Math.abs(Number(unit.startMs ?? 0) - atMs);
        const endDistance = Math.abs(Number(unit.endMs ?? 0) - atMs);
        const distance = Math.min(startDistance, endDistance);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
        }
    }
    return bestIndex;
};

const findWordIndexForStart = (words: TimedTranscriptWord[], startMs: number): number => {
    for (let index = 0; index < words.length; index += 1) {
        const word = words[index];
        if (Number(word.endMs ?? 0) >= startMs - EPS_MS) {
            return index;
        }
    }
    return words.length - 1;
};

const findWordIndexForEnd = (words: TimedTranscriptWord[], endMs: number): number => {
    for (let index = 0; index < words.length; index += 1) {
        const word = words[index];
        if (Number(word.endMs ?? 0) >= endMs - EPS_MS) {
            return index;
        }
    }
    return words.length - 1;
};

const refineStartMs = (
    candidate: RangeCandidate,
    units: TimedTranscriptUnit[],
    words: TimedTranscriptWord[],
): number => {
    const rawStartMs = clampPositiveMs(Number(candidate.startMs ?? 0));
    if (words.length === 0) return rawStartMs;

    const startUnitIndex = findUnitIndex(units, candidate.startUnitId, rawStartMs);
    const startUnit = startUnitIndex >= 0 ? units[startUnitIndex] : null;
    if (
        startUnit &&
        rawStartMs >= Number(startUnit.startMs ?? 0) &&
        rawStartMs - Number(startUnit.startMs ?? 0) <= START_UNIT_SNAP_BACK_MS
    ) {
        return clampPositiveMs(Number(startUnit.startMs ?? rawStartMs));
    }

    const wordIndex = findWordIndexForStart(words, rawStartMs);
    const word = wordIndex >= 0 ? words[wordIndex] : null;
    const prevWord = wordIndex > 0 ? words[wordIndex - 1] : null;
    const wordStartMs = clampPositiveMs(Number(word?.startMs ?? rawStartMs));
    const prevWordEndMs = clampPositiveMs(Number(prevWord?.endMs ?? 0));
    const gapBeforeMs = Math.max(0, wordStartMs - prevWordEndMs);

    if (gapBeforeMs >= START_MIN_GAP_MS) {
        return clampPositiveMs(Math.max(prevWordEndMs, wordStartMs - START_PREROLL_MS));
    }

    return rawStartMs;
};

const refineEndMs = (
    candidate: RangeCandidate,
    units: TimedTranscriptUnit[],
    words: TimedTranscriptWord[],
): number => {
    const rawEndMs = clampPositiveMs(Number(candidate.endMs ?? 0));
    if (words.length === 0) {
        const endUnitIndex = findUnitIndex(units, candidate.endUnitId, rawEndMs);
        const endUnit = endUnitIndex >= 0 ? units[endUnitIndex] : null;
        if (
            endUnit &&
            Number(endUnit.endMs ?? rawEndMs) >= rawEndMs &&
            Number(endUnit.endMs ?? rawEndMs) - rawEndMs <= END_UNIT_SNAP_EXTENSION_MS
        ) {
            return clampPositiveMs(Number(endUnit.endMs ?? rawEndMs));
        }
        return rawEndMs;
    }

    const endWordIndex = findWordIndexForEnd(words, rawEndMs);
    const startIndex = Math.max(0, endWordIndex);

    for (
        let index = startIndex;
        index < words.length && index <= startIndex + MAX_WORD_SCAN_AHEAD;
        index += 1
    ) {
        const word = words[index];
        if (!word) continue;

        const boundaryEndMs = clampPositiveMs(Number(word.endMs ?? rawEndMs));
        const extensionMs = Math.max(0, boundaryEndMs - rawEndMs);
        if (extensionMs > END_MAX_EXTENSION_MS) break;

        const nextWord = index + 1 < words.length ? words[index + 1] : null;
        const gapAfterMs = getGapMs(boundaryEndMs, nextWord?.startMs);
        const hardStop = isHardStopText(word.text);
        const softStop = isSoftStopText(word.text);
        const speakerTurn =
            !!nextWord &&
            String(nextWord.speakerName ?? "").trim() &&
            String(word.speakerName ?? "").trim() &&
            String(nextWord.speakerName ?? "").trim() !== String(word.speakerName ?? "").trim();

        const isNaturalBoundary =
            hardStop ||
            gapAfterMs >= END_PAUSE_MS ||
            (speakerTurn && gapAfterMs >= END_TURN_GAP_MS) ||
            (softStop && extensionMs >= 120);

        if (isNaturalBoundary) {
            return clampTailWithinGap(boundaryEndMs, gapAfterMs);
        }
    }

    const endUnitIndex = findUnitIndex(units, candidate.endUnitId, rawEndMs);
    const endUnit = endUnitIndex >= 0 ? units[endUnitIndex] : null;
    if (
        endUnit &&
        Number(endUnit.endMs ?? rawEndMs) >= rawEndMs &&
        Number(endUnit.endMs ?? rawEndMs) - rawEndMs <= END_UNIT_SNAP_EXTENSION_MS
    ) {
        const nextUnit = endUnitIndex + 1 < units.length ? units[endUnitIndex + 1] : null;
        const gapAfterUnitMs = getGapMs(Number(endUnit.endMs ?? rawEndMs), nextUnit?.startMs);
        return clampTailWithinGap(Number(endUnit.endMs ?? rawEndMs), gapAfterUnitMs);
    }

    const endWord = words[startIndex] ?? words[words.length - 1];
    const nextWord = startIndex + 1 < words.length ? words[startIndex + 1] : null;
    const gapAfterMs = getGapMs(Number(endWord?.endMs ?? rawEndMs), nextWord?.startMs);
    if (gapAfterMs >= END_MIN_GAP_MS) {
        return clampTailWithinGap(rawEndMs, gapAfterMs);
    }

    return rawEndMs;
};

export const refineCreateClipRange = (args: {
    candidate: RangeCandidate;
    transcriptUnits: TimedTranscriptUnit[];
    transcriptWords: TimedTranscriptWord[];
}): RefinedRange => {
    const candidate = args.candidate;
    const transcriptUnits = Array.isArray(args.transcriptUnits) ? args.transcriptUnits : [];
    const transcriptWords = Array.isArray(args.transcriptWords) ? args.transcriptWords : [];

    const startMs = refineStartMs(candidate, transcriptUnits, transcriptWords);
    const endMs = refineEndMs(candidate, transcriptUnits, transcriptWords);

    if (endMs <= startMs) {
        return {
            startMs: clampPositiveMs(Number(candidate.startMs ?? 0)),
            endMs: clampPositiveMs(Math.max(Number(candidate.endMs ?? 0), Number(candidate.startMs ?? 0))),
        };
    }

    return { startMs, endMs };
};
